import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskLine, Reveal } from "@/components/site/Reveal";
import { Breadcrumb, breadcrumbLd } from "@/components/site/Breadcrumb";
import { useSeo, SITE_URL, ORGANIZATION } from "@/lib/seo";
import { PRESS_URL, ABOUT_INTRO, ROOTS, STEPS, VALUES } from "@/lib/about";
import { ExpertiseBlock } from "@/components/site/Expertise";

const crumbs = [{ to: "/", label: "Accueil" }, { to: "/a-propos", label: "À propos" }];

export default function About() {
  useSeo({
    title: "À propos d'Ozentya : l'histoire de Pascal Chaubo | Ozentya",
    description: "Ozentya, marque familiale antillaise de soins pour cheveux afro et crépus fondée en 2011 par Pascal Chaubo : son histoire, l'hommage à sa grand-mère Ozange, nos soins fabriqués en France.",
    path: "/a-propos",
    jsonLd: { "@context": "https://schema.org", "@graph": [
      { "@type": "AboutPage", name: "À propos d'Ozentya", url: `${SITE_URL}/a-propos`, about: { "@id": ORGANIZATION["@id"] } },
      { ...ORGANIZATION, foundingDate: "2011", subjectOf: { "@type": "NewsArticle", headline: "Le pari Ozentya", url: PRESS_URL, publisher: { "@type": "Organization", name: "France-Antilles" }, datePublished: "2026-09-16" } },
      breadcrumbLd(SITE_URL, crumbs),
    ] },
  });
  return (
    <main data-testid="about-page" className="mx-auto max-w-[1440px] px-5 pb-32 pt-44 md:px-10">
      <Breadcrumb items={crumbs} />
      <p className="eyebrow mb-6">Notre histoire</p>
      <h1 className="max-w-5xl font-display text-4xl leading-[1.05] sm:text-5xl lg:text-6xl"><MaskLine delay={0.05}>Ozentya, l'histoire d'un pari</MaskLine></h1>
      <p className="mt-8 max-w-3xl text-base leading-relaxed text-[var(--parchment)]/85 md:text-lg">{ABOUT_INTRO}</p>
      <div className="mt-10 max-w-4xl"><ExpertiseBlock testid="about-expertise" /></div>

      <section data-testid="about-roots" className="mt-16 border-l-2 border-[var(--gold)] pl-6 md:pl-10">
        <p className="eyebrow mb-4">Nos racines</p>
        <h2 className="font-display text-3xl sm:text-4xl">Une histoire antillaise, <span className="italic gold-text">transmise en famille</span></h2>
        {ROOTS.map((t) => <p key={t} className="mt-5 max-w-3xl text-base leading-relaxed text-[var(--parchment)]/85">{t}</p>)}
      </section>

      <section data-testid="about-story" className="mt-20 border-t border-[var(--line)] pt-16">
        <h2 className="font-display text-3xl sm:text-4xl">De l'importation à la création d'une marque</h2>
        <ol className="mt-12 space-y-12 border-l border-[var(--line)] pl-8 md:pl-12">
          {STEPS.map((s) => (
            <li key={s.title} className="relative max-w-3xl">
              <span className="absolute -left-[38px] top-1.5 h-3 w-3 rounded-full bg-[var(--gold)] md:-left-[54px]" aria-hidden="true" />
              <p className="eyebrow mb-2">{s.year}</p>
              <h3 className="font-display text-2xl italic text-[var(--gold-soft)]">{s.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[var(--parchment)]/85 md:text-base">{s.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <Reveal className="mt-24">
        <blockquote className="max-w-4xl border-l-2 border-[var(--gold)] pl-6 font-display text-3xl italic leading-snug sm:text-4xl">« C'est tout naturellement grâce à mon oncle que je suis entré dans le monde des affaires. »</blockquote>
        <p className="mt-4 pl-6 text-xs uppercase tracking-[0.2em] text-[var(--parchment)]/70">Pascal Chaubo, fondateur, dans France-Antilles</p>
      </Reveal>

      <section data-testid="about-values" className="mt-24">
        <h2 className="font-display text-3xl sm:text-4xl">Nos engagements</h2>
        <div className="mt-10 grid gap-px overflow-hidden rounded-sm border border-[var(--line)] bg-[var(--line)] sm:grid-cols-2">
          {VALUES.map((v) => (
            <div key={v.title} className="bg-[var(--ink)] p-6 md:p-8">
              <h3 className="font-display text-2xl italic text-[var(--gold-soft)]">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[var(--parchment)]/85">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="about-press" className="mt-24 grid gap-8 border-t border-[var(--line)] pt-16 lg:grid-cols-2">
        <div>
          <h2 className="font-display text-3xl sm:text-4xl">Ils parlent de nous</h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-[var(--parchment)]/85 md:text-base">En septembre 2026, France-Antilles a consacré un portrait à Pascal Chaubo et au parcours d'Ozentya.</p>
          <a href={PRESS_URL} target="_blank" rel="noopener noreferrer" data-testid="about-press-link" className="mt-6 inline-flex items-center gap-2 rounded-full border border-[var(--gold)] px-6 py-3 text-xs uppercase tracking-[0.2em] text-[var(--gold-soft)] transition-colors hover:bg-[var(--gold)] hover:text-[var(--ink)]">
            Lire « Le pari Ozentya » <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
        <div>
          <h2 className="font-display text-3xl sm:text-4xl">Découvrir nos soins</h2>
          <ul className="mt-6 space-y-3 text-sm md:text-base">
            <li><Link to="/pousse-des-cheveux" className="text-[var(--gold-soft)] underline underline-offset-4">Notre expertise de la pousse des cheveux</Link></li>
            <li><Link to="/cheveux-afro-crepus" className="text-[var(--gold-soft)] underline underline-offset-4">Soins pour cheveux afro et crépus</Link></li>
            <li><Link to="/chute-de-cheveux-alopecie" className="text-[var(--gold-soft)] underline underline-offset-4">Chute de cheveux et alopécie : nos repères</Link></li>
            <li><Link to="/conseils" className="text-[var(--gold-soft)] underline underline-offset-4">Tous nos conseils et routines</Link></li>
          </ul>
        </div>
      </section>
    </main>
  );
}
